/**
 * SendEmailDialog — modal for preparing the email that goes out with
 * the meeting minutes. The dialog reviews recipients + subject + body
 * + attachments, then triggers a server-side .eml download which the
 * user double-clicks to open in Outlook Desktop.
 *
 * Fields:
 *   To / CC — comma-separated email addresses
 *   Subject — prefilled from meeting title; editable
 *   Body — plain text; auto-fetched from POST /meetings/:id/generate-summary
 *          on first open (cached on the meeting row for re-opens)
 *   Attachments — .docx + .pdf toggled on by default
 *   Variant — client_meeting | internal (matches current templateVariant)
 *
 * Resets on each open so re-opening after a successful download shows
 * the form again, not the stale success panel.
 */
import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Mail } from 'lucide-react';
import { ApiError, api, postAndDownload } from '../../lib/apiClient.ts';
import { NO_AUTOFILL } from '../../lib/noAutofill.ts';
import { Modal, ModalHeader } from '../ui/Modal.tsx';

interface Props {
  meetingId: string;
  meetingTitle: string;
  defaultSubject: string;
  defaultBody: string;
  defaultVariant: 'client_meeting' | 'internal';
  /** Prefilled comma-joined recipients (non-OJB emails from calendar capture). */
  defaultTo?: string;
  /** Prefilled comma-joined CC (OJB emails from calendar capture). */
  defaultCc?: string;
  isOpen: boolean;
  onClose: () => void;
  /** Called after the Outlook draft is successfully created and opened.
   *  Parent uses this to refresh meeting state (server auto-finalizes
   *  on draft creation per the "emailing = committing" product rule). */
  onSent?: () => void;
}

export function SendEmailDialog({
  meetingId,
  meetingTitle,
  defaultSubject,
  defaultBody,
  defaultVariant,
  defaultTo = '',
  defaultCc = '',
  isOpen,
  onClose,
  onSent,
}: Props) {
  const [to, setTo] = useState(defaultTo);
  const [cc, setCc] = useState(defaultCc);
  const [subject, setSubject] = useState(defaultSubject);
  const [body, setBody] = useState(defaultBody);
  const [variant, setVariant] = useState<'client_meeting' | 'internal'>(defaultVariant);
  const [attachDocx, setAttachDocx] = useState(true);
  const [attachPdf, setAttachPdf] = useState(true);
  /** Tracks whether the user has manually typed in the body field. If
   *  yes, we won't overwrite their text when the auto-fetched summary
   *  arrives (or when they re-open the dialog later). */
  const [bodyTouched, setBodyTouched] = useState(false);

  /**
   * Fetch the auto-generated email body summary on dialog open. Uses
   * the cached summary on the meeting row when present (POST returns
   * cached: true) so a re-open is instant; the first-ever call may
   * take a couple of seconds while Claude Haiku writes the paragraph.
   */
  const summary = useQuery({
    queryKey: ['email-summary', meetingId],
    queryFn: () =>
      api.post<{ subject: string; body: string; cached: boolean }>(
        `/meetings/${meetingId}/generate-summary`,
        {},
      ),
    enabled: isOpen && !!meetingId,
    staleTime: 10 * 60 * 1000,
    retry: false,
  });

  /**
   * Trigger the .eml download. The server returns a multipart/mixed .eml
   * with X-Unsent: 1 header + the .docx + .pdf as attachments. The
   * browser downloads it; the user double-clicks the file and Outlook
   * Desktop opens a native compose window pre-populated with the
   * recipients, subject, body, and attachments.
   *
   * Side-channel headers on the response:
   *   X-Meeting-Finalized          — "true" / "false"
   *   X-Meeting-Finalize-Conflict  — "conflict" | "db_conflict" (optional)
   * We surface those in the success panel so the user knows whether the
   * meeting was auto-finalized.
   */
  const prepareDraft = useMutation({
    mutationFn: async () => {
      const attach: Array<'docx' | 'pdf'> = [];
      if (attachDocx) attach.push('docx');
      if (attachPdf) attach.push('pdf');
      const result = await postAndDownload(
        `/meetings/${meetingId}/send-email`,
        {
          to: parseEmails(to),
          cc: cc.trim() ? parseEmails(cc) : undefined,
          subject: subject.trim() || undefined,
          body: body.trim() || undefined,
          attach,
          variant,
        },
        `${meetingTitle || 'meeting-minutes'}.eml`,
      );
      return {
        filename: result.filename,
        finalized: result.headers.get('X-Meeting-Finalized') === 'true',
        finalizeConflictKind: result.headers.get('X-Meeting-Finalize-Conflict') as
          | 'conflict'
          | 'db_conflict'
          | null,
      };
    },
    onSuccess: () => {
      onSent?.();
    },
  });

  // Reset the form + mutation each time the dialog opens. Without
  // resetting prepareDraft, re-opening after a successful download would
  // show the stale "Draft downloaded" panel instead of the form.
  useEffect(() => {
    if (isOpen) {
      setTo(defaultTo);
      setCc(defaultCc);
      setSubject(defaultSubject);
      setBody(defaultBody);
      setVariant(defaultVariant);
      setAttachDocx(true);
      setAttachPdf(true);
      setBodyTouched(false);
      prepareDraft.reset();
    }
    // Intentionally narrow deps: prepareDraft excluded so its
    // mutation-state changes don't re-fire the reset. (Was paired
    // with an eslint-disable for react-hooks/exhaustive-deps; the
    // rule isn't currently loaded so the disable was removed.)
  }, [isOpen, defaultSubject, defaultBody, defaultVariant, defaultTo, defaultCc]);

  // When the auto-generated summary arrives, slide it into the body
  // field — but only if the user hasn't started typing in body yet.
  useEffect(() => {
    if (
      isOpen &&
      summary.data?.body &&
      !bodyTouched &&
      (!body || body === defaultBody)
    ) {
      setBody(summary.data.body);
    }
  }, [isOpen, summary.data?.body]);

  if (!isOpen) return null;

  const errMsg =
    prepareDraft.error instanceof ApiError
      ? prepareDraft.error.message
      : prepareDraft.error instanceof Error
        ? prepareDraft.error.message
        : null;
  const reauth =
    prepareDraft.error instanceof ApiError && prepareDraft.error.status === 401;
  // Detailed parse so the To/CC strips can also show entries
  // that failed validation — silent rejection used to be the
  // root cause of "the email never went to Bob" complaints.
  const toParsed = parseEmailsDetailed(to);
  const ccParsed = parseEmailsDetailed(cc);
  const toList = toParsed.valid;
  const ccList = ccParsed.valid;
  const canOpen =
    toList.length > 0 && (attachDocx || attachPdf) && !prepareDraft.isPending;

  // Security audit M5: identify external recipients (anything not
  // @ojb.com) so the UI can flag the user before they send firm-
  // sensitive content out of the tenant. Pilot uses this as a
  // soft warning — no hard block, since legitimate client emails
  // are the whole point of meeting minutes — but visibility is
  // important when an AI-generated default body could have
  // suggested an external address the user didn't notice.
  const externalRecipients = [...toList, ...ccList].filter(
    (email) => !email.toLowerCase().endsWith('@ojb.com'),
  );

  /**
   * Click handler for the primary button. Triggers the .eml download.
   * The browser handles the file download; the user then double-clicks
   * the resulting .eml to open Outlook Desktop in compose mode.
   */
  function onOpenInOutlook() {
    if (!canOpen) return;
    prepareDraft.mutate();
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      ariaLabel="Prepare email in Outlook"
    >
      <ModalHeader
        title="Open in Outlook"
        subtitle="Downloads an .eml file with recipients, body, and attachments ready to go. Double-click it and Outlook Desktop opens a native compose window - review and hit Send from there."
        onClose={onClose}
      />

      <div className="flex flex-1 flex-col overflow-hidden">
        {prepareDraft.isSuccess ? (
          <div className="flex flex-col items-center gap-3 p-10 text-center">
            <div className="rounded-full bg-pine/10 px-4 py-2 text-sm font-semibold text-pine">
              ✓ Draft downloaded
              {prepareDraft.data?.finalized ? ' & Finalized' : ''}
            </div>
            <div className="max-w-md text-xs text-smoke">
              Double-click the downloaded file
              {prepareDraft.data?.filename ? (
                <>
                  {' '}(<code className="text-charcoal">
                    {prepareDraft.data.filename}
                  </code>)
                </>
              ) : null}{' '}
              to open it in Outlook. The compose window will pop up with
              recipients, body, and attachments already filled in - review and
              hit Send from there.
              {prepareDraft.data?.finalized &&
                ' This meeting has been moved to Finalized.'}
            </div>
            {prepareDraft.data?.finalizeConflictKind && (
              <div className="max-w-md rounded border border-yellow-300 bg-yellow-50 px-3 py-2 text-left text-[11px] text-yellow-900">
                <div className="font-semibold">
                  Draft ready - but finalize was blocked
                </div>
                <div className="mt-1">
                  {prepareDraft.data.finalizeConflictKind === 'conflict'
                    ? 'Another finalized meeting has a similar title on the ' +
                      'same date + project. The meeting stays a draft. Open ' +
                      'it and hit Finalize when ready to override.'
                    : 'A finalized meeting with an exact title + date + project ' +
                      'match already exists. Change the title or date to proceed.'}
                </div>
              </div>
            )}
            <button
              type="button"
              onClick={onClose}
              className="mt-2 rounded bg-pine px-4 py-2 text-sm font-medium text-cloud transition-colors hover:bg-pine/90"
            >
              Close
            </button>
          </div>
        ) : (
          <div className="flex flex-col gap-4 overflow-y-auto px-5 py-4 text-sm">
            <Field label="To" required>
              <input
                {...NO_AUTOFILL}
                type="text"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                placeholder="name@firm.com, name2@firm.com"
                className="w-full rounded-lg border border-charcoal/20 bg-paper-light px-3 py-2 text-sm text-charcoal focus:border-pine focus:outline-none focus:ring-2 focus:ring-pine/20"
                autoFocus
              />
              {toList.length > 0 && (
                <div className="mt-1 text-[11px] text-smoke">
                  {toList.length} recipient{toList.length === 1 ? '' : 's'}
                </div>
              )}
              <RejectedEmailsHint
                rejected={toParsed.invalid}
                fieldLabel="To"
              />
            </Field>

            <Field label="CC">
              <input
                {...NO_AUTOFILL}
                type="text"
                value={cc}
                onChange={(e) => setCc(e.target.value)}
                placeholder="optional"
                className="w-full rounded-lg border border-charcoal/20 bg-paper-light px-3 py-2 text-sm text-charcoal focus:border-pine focus:outline-none focus:ring-2 focus:ring-pine/20"
              />
              {ccList.length > 0 && (
                <div className="mt-1 text-[11px] text-smoke">
                  {ccList.length} on CC
                </div>
              )}
              <RejectedEmailsHint
                rejected={ccParsed.invalid}
                fieldLabel="CC"
              />
            </Field>

            <Field label="Subject">
              <input
                {...NO_AUTOFILL}
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder={`Meeting Minutes - ${meetingTitle}`}
                className="w-full rounded-lg border border-charcoal/20 bg-paper-light px-3 py-2 text-sm text-charcoal focus:border-pine focus:outline-none focus:ring-2 focus:ring-pine/20"
              />
            </Field>

            <Field label="Message">
              <textarea
                {...NO_AUTOFILL}
                value={body}
                onChange={(e) => {
                  setBody(e.target.value);
                  setBodyTouched(true);
                }}
                placeholder={
                  summary.isLoading
                    ? 'Drafting a short summary from the minutes…'
                    : 'Add a short note to the recipients.'
                }
                rows={8}
                className="w-full resize-y rounded-lg border border-charcoal/20 bg-paper-light px-3 py-2 text-sm text-charcoal focus:border-pine focus:outline-none"
              />
              <div className="mt-1 flex items-center justify-between text-[11px] text-smoke">
                <span>
                  {summary.isLoading
                    ? 'Generating summary from the minutes…'
                    : summary.data?.cached
                      ? 'Pre-filled from the cached summary - edit freely.'
                      : summary.data
                        ? 'Pre-filled from the meeting minutes - edit freely.'
                        : ''}
                </span>
                {summary.data && (
                  <button
                    type="button"
                    className="underline-offset-2 hover:underline"
                    onClick={() => {
                      // Re-fetch with force=true via a custom call so
                      // the user can ask for a fresh draft.
                      api
                        .post<{ body: string }>(
                          `/meetings/${meetingId}/generate-summary`,
                          { force: true },
                        )
                        .then((r) => {
                          setBody(r.body);
                          setBodyTouched(false);
                          summary.refetch();
                        })
                        .catch(() => {
                          // Best-effort; stay silent on failure (the
                          // user can write their own body).
                        });
                    }}
                  >
                    Regenerate
                  </button>
                )}
              </div>
            </Field>

            <div className="grid grid-cols-2 gap-4">
              <Field label="Attachments">
                <div className="flex flex-col gap-1.5">
                  <label className="flex items-center gap-2 text-xs">
                    <input
                      type="checkbox"
                      checked={attachDocx}
                      onChange={(e) => setAttachDocx(e.target.checked)}
                    />
                    Word (.docx)
                  </label>
                  <label className="flex items-center gap-2 text-xs">
                    <input
                      type="checkbox"
                      checked={attachPdf}
                      onChange={(e) => setAttachPdf(e.target.checked)}
                    />
                    PDF
                  </label>
                </div>
              </Field>

              {/* Variant select hidden for the pilot — single
                  unified format. The variant state stays so we can
                  reintroduce the choice later without rebuilding
                  this dialog's plumbing. */}
            </div>

            {/* Security audit M5: warn when any recipient is outside
                the OJB tenant. Soft warning only — sending external
                client emails is legitimate, but an AI-generated
                default body could have suggested an address the
                user didn't notice, and a brief glance at this
                banner makes the decision deliberate. */}
            {externalRecipients.length > 0 && (
              <div className="rounded-lg border border-terra-cotta/30 bg-terra-cotta/[0.08] px-3 py-2 text-[12px] text-charcoal">
                <div className="font-semibold text-terra-cotta">
                  External recipients
                </div>
                <div className="mt-0.5 text-charcoal/80">
                  These addresses are outside @ojb.com:{' '}
                  <span className="font-medium">
                    {externalRecipients.join(', ')}
                  </span>
                  . Confirm before sending.
                </div>
              </div>
            )}

            {reauth && (
              <div className="rounded border border-yellow-300 bg-yellow-50 px-3 py-2 text-xs text-yellow-900">
                Microsoft sign-in expired. Sign out and sign in again to grant
                send-email access.
              </div>
            )}
            {errMsg && !reauth && (
              <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                {errMsg}
              </div>
            )}
          </div>
        )}

        {!prepareDraft.isSuccess && (
          <div className="flex items-center justify-between gap-3 border-t border-smoke/30 px-5 py-3">
            <span className="text-[11px] text-smoke">
              Downloads an .eml. Double-click to open Outlook Desktop with a
              compose window already prepared.
            </span>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={onClose}
                className="rounded-full border border-charcoal/25 px-4 py-2 text-sm text-charcoal transition-colors hover:border-pine hover:text-pine"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={onOpenInOutlook}
                disabled={!canOpen}
                className="inline-flex items-center gap-1.5 rounded-full bg-pine px-4 py-2 text-sm font-semibold text-cloud transition-colors hover:bg-pine/90 disabled:opacity-50"
              >
                {prepareDraft.isPending ? (
                  'Preparing draft…'
                ) : (
                  <>
                    <Mail size={14} />
                    Download for Outlook
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}

function Field({
  label,
  children,
  required,
}: {
  label: string;
  children: React.ReactNode;
  required?: boolean;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-charcoal/70">
        {label}
        {required && <span className="ml-1 text-pine">*</span>}
      </span>
      {children}
    </label>
  );
}

/**
 * Inline strip below the To / CC inputs that shows entries the
 * validator rejected, with a short "missing @" / "missing
 * top-level domain" / etc. hint each. Capped at 5 entries with
 * a "+N more" tail so a paste-from-Excel doesn't flood the
 * dialog. Hidden when there are no rejections.
 */
function RejectedEmailsHint({
  rejected,
  fieldLabel,
}: {
  rejected: Array<{ raw: string; reason: string }>;
  fieldLabel: string;
}) {
  if (rejected.length === 0) return null;
  const visible = rejected.slice(0, 5);
  const overflow = rejected.length - visible.length;
  return (
    <div
      role="status"
      className="mt-2 rounded border border-amber-300 bg-amber-50 px-2.5 py-1.5 text-[11px] text-amber-900"
    >
      <div className="font-semibold">
        Skipped {rejected.length} {fieldLabel.toLowerCase()} entr
        {rejected.length === 1 ? 'y' : 'ies'}
      </div>
      <ul className="mt-0.5 space-y-0.5">
        {visible.map((r, i) => (
          <li key={`${r.raw}-${i}`} className="flex items-baseline gap-2">
            <span className="font-mono text-amber-950 truncate max-w-[60%]">
              {r.raw}
            </span>
            <span className="text-amber-800/80">- {r.reason}</span>
          </li>
        ))}
        {overflow > 0 && (
          <li className="italic text-amber-800/80">
            …and {overflow} more
          </li>
        )}
      </ul>
    </div>
  );
}

/**
 * WHATWG/HTML5 email validation regex — same one Chrome /
 * Firefox / Safari use for `<input type="email">`. Stricter
 * than our previous `[^@\s]+@[^@\s]+\.[^@\s]+` (which silently
 * accepted edge cases like `..@example.com` and `.@.`) but more
 * compatible with real RFC 5322-shaped addresses (proper local
 * part, multi-label domains, valid label rules).
 */
const EMAIL_RE =
  /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

export interface ParsedEmails {
  valid: string[];
  invalid: Array<{ raw: string; reason: string }>;
}

/** Classify why a token failed parsing - short, user-readable
 *  hints so the rejected-list strip can show "missing @",
 *  "missing top-level domain", etc. instead of just "invalid". */
function explainEmailFailure(token: string): string {
  if (!token.includes('@')) return 'missing @';
  const [, domain] = token.split('@');
  if (!domain || domain.length === 0) return 'missing domain';
  if (!domain.includes('.')) return 'missing top-level domain';
  if (domain.endsWith('.') || domain.startsWith('.')) {
    return 'leading or trailing dot in domain';
  }
  if (token.startsWith('.') || token.includes('..')) {
    return 'invalid dot placement';
  }
  return 'invalid format';
}

/**
 * Parse a free-text recipient field into valid + invalid lists.
 * Splits on commas, semicolons, whitespace; rejected tokens come
 * back with a short reason so the dialog can show what got
 * dropped instead of silently truncating the count.
 */
export function parseEmailsDetailed(s: string): ParsedEmails {
  const tokens = s
    .split(/[,\s;]+/)
    .map((x) => x.trim())
    .filter((x) => x.length > 0);
  const valid: string[] = [];
  const invalid: Array<{ raw: string; reason: string }> = [];
  for (const t of tokens) {
    if (EMAIL_RE.test(t)) {
      valid.push(t);
    } else {
      invalid.push({ raw: t, reason: explainEmailFailure(t) });
    }
  }
  return { valid, invalid };
}

/** Convenience wrapper for callers that only need the valid list
 *  (e.g. the prepareDraft mutation that POSTs to the backend).
 *  Existing call sites keep working unchanged. */
function parseEmails(s: string): string[] {
  return parseEmailsDetailed(s).valid;
}
